import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useStore } from '../../context/StoreContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  Plus, Pencil, Trash, Camera, CircleNotch, User, Envelope, Phone, IdentificationBadge, FloppyDisk
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = { first_name: '', last_name: '', email: '', phone: '', avatar_url: '', is_active: true };

const CommerciauxPage = () => {
  const { currentStore } = useStore();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchList = useCallback(async () => {
    if (!currentStore) return;
    try {
      const r = await axios.get(`${API_URL}/api/stores/${currentStore.id}/commerciaux`, { withCredentials: true });
      setList(r.data || []);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, [currentStore]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      first_name: c.first_name || '',
      last_name: c.last_name || '',
      email: c.email || '',
      phone: c.phone || '',
      avatar_url: c.avatar_url || '',
      is_active: c.is_active !== false,
    });
    setOpen(true);
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, avatar_url: data.url }));
      toast.success('Photo téléchargée');
    } catch { toast.error("Impossible d'uploader la photo"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.first_name?.trim() || !form.last_name?.trim()) {
      toast.error('Nom et prénom obligatoires');
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API_URL}/api/stores/${currentStore.id}/commerciaux/${editId}`, form, { withCredentials: true });
        toast.success('Commercial mis à jour');
      } else {
        await axios.post(`${API_URL}/api/stores/${currentStore.id}/commerciaux`, form, { withCredentials: true });
        toast.success('Commercial créé');
      }
      setOpen(false);
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer ${c.first_name} ${c.last_name} ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/stores/${currentStore.id}/commerciaux/${c.id}`, { withCredentials: true });
      toast.success('Commercial supprimé');
      fetchList();
    } catch { toast.error('Erreur'); }
  };

  const avatarSrc = (url) => url ? (url.startsWith('/') ? `${API_URL}${url}` : url) : null;

  if (!currentStore) return <div className="text-center py-12 text-slate-500">Sélectionnez une boutique</div>;

  return (
    <div className="space-y-6" data-testid="commerciaux-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Équipe commerciale</h1>
          <p className="text-slate-500 mt-1">Gérez les fiches publiques de vos conseillers commerciaux.</p>
        </div>
        <Button onClick={openCreate} className="bg-[#0052FF] hover:bg-[#0040CC] gap-2" data-testid="add-commercial-btn">
          <Plus size={16} /> Nouveau commercial
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12"><CircleNotch className="animate-spin mx-auto text-slate-400" size={28} /></div>
      ) : list.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <IdentificationBadge size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 mb-4">Aucun commercial enregistré</p>
            <Button onClick={openCreate} className="bg-[#0052FF] hover:bg-[#0040CC] gap-2">
              <Plus size={16} /> Ajouter un commercial
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map(c => {
            const src = avatarSrc(c.avatar_url);
            const initials = ((c.first_name?.[0] || '') + (c.last_name?.[0] || '')).toUpperCase() || 'C';
            return (
              <Card key={c.id} className={`relative ${c.is_active === false ? 'opacity-60' : ''}`} data-testid={`commercial-card-${c.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-md flex items-center justify-center text-white font-bold text-xl" style={{ backgroundColor: '#c2185b' }}>
                      {src ? <img src={src} alt={`${c.first_name} ${c.last_name}`} className="w-full h-full object-cover" /> : initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900">{c.first_name} {c.last_name}</p>
                      {c.email && (
                        <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0052FF] mt-1 truncate">
                          <Envelope size={12} /> {c.email}
                        </a>
                      )}
                      {c.phone && (
                        <a href={`tel:${c.phone.replace(/\s/g, '')}`} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0052FF] mt-0.5">
                          <Phone size={12} /> {c.phone}
                        </a>
                      )}
                      {c.is_active === false && <span className="inline-block mt-2 px-2 py-0.5 text-[10px] font-bold rounded bg-slate-100 text-slate-500">INACTIF</span>}
                    </div>
                  </div>
                  <div className="flex justify-end gap-1 mt-3 pt-3 border-t">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-commercial-${c.id}`}>
                      <Pencil size={14} className="mr-1" /> Modifier
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(c)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-commercial-${c.id}`}>
                      <Trash size={14} className="mr-1" /> Supprimer
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── Dialog form ── */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="commercial-form-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? 'Modifier le commercial' : 'Nouveau commercial'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {/* Avatar */}
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white shadow-md flex items-center justify-center text-white font-bold text-2xl" style={{ backgroundColor: '#c2185b' }}>
                {form.avatar_url
                  ? <img src={avatarSrc(form.avatar_url)} alt="avatar" className="w-full h-full object-cover" />
                  : ((form.first_name?.[0] || '') + (form.last_name?.[0] || '')).toUpperCase() || <User size={28} />
                }
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => uploadAvatar(e.target.files?.[0])}
                  data-testid="commercial-avatar-input"
                />
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-2">
                  {uploading ? <CircleNotch className="animate-spin" size={14} /> : <Camera size={14} />}
                  Changer la photo
                </Button>
                {form.avatar_url && (
                  <Button variant="ghost" size="sm" onClick={() => setForm(f => ({ ...f, avatar_url: '' }))} className="text-red-600 hover:bg-red-50 justify-start">
                    Supprimer la photo
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Prénom *</Label>
                <Input
                  value={form.first_name}
                  onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                  placeholder="Jean"
                  data-testid="commercial-first-name"
                />
              </div>
              <div>
                <Label className="text-sm">Nom *</Label>
                <Input
                  value={form.last_name}
                  onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                  placeholder="Dupont"
                  data-testid="commercial-last-name"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm flex items-center gap-1.5"><Envelope size={14} /> Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="jean@mrg-istres.fr"
                data-testid="commercial-email"
              />
            </div>

            <div>
              <Label className="text-sm flex items-center gap-1.5"><Phone size={14} /> Téléphone</Label>
              <Input
                type="tel"
                value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="06 12 34 56 78"
                data-testid="commercial-phone"
              />
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <Label className="text-sm">Fiche active (affichée publiquement)</Label>
              <Switch
                checked={!!form.is_active}
                onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))}
                data-testid="commercial-active-switch"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#0052FF] hover:bg-[#0040CC] gap-2"
              data-testid="save-commercial-btn"
            >
              {saving ? <CircleNotch className="animate-spin" size={16} /> : <FloppyDisk size={16} />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommerciauxPage;
